import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown, ArrowRight, Linkedin } from "lucide-react";
import BrandMark from "../ui/BrandMark";
import { EASE } from "../ui/Reveal";
import team from "../../data/team.json";
import useLang from "../../hooks/useLang";

// "About" as a small panel: the vision line, then the founders dropping in under "Meet the Builders".
const BehindTheLightMenu = () => {
  const { t, isRTL } = useLang();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="behind-the-light-panel"
        className={`flex items-center gap-1.5 h-11 px-3 rounded-xl text-sm font-semibold transition-colors ${
          open ? "text-white bg-white/10" : "text-white/70 hover:text-white"
        }`}
      >
        {t("nav.behindTheLight")}
        <ChevronDown size={15} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <motion.div
          id="behind-the-light-panel"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: EASE }}
          className="absolute end-0 top-[calc(100%+10px)] z-50 w-[min(92vw,480px)] rounded-2xl border border-white/10 bg-[#141414] p-5 shadow-2xl"
        >
          <div className="flex items-start gap-4">
            <BrandMark variant="onDark" animated className="w-14 h-auto flex-shrink-0" />
            <div className="min-w-0">
              <div className="font-mono text-[10px] tracking-[0.12em] text-rose-light uppercase">
                {t("visionMission.visionTitle")}
              </div>
              <p className="text-[15px] font-semibold text-white leading-snug mt-1.5">{t("visionMission.vision")}</p>
            </div>
          </div>

          <div className="mt-5 pt-4 border-t border-white/10">
            <div className="font-mono text-[10px] tracking-[0.12em] text-white/45 uppercase mb-3">
              {t("nav.meetBuilders")}
            </div>
            <ul className="space-y-2">
              {team.map((m, i) => {
                const name = isRTL ? m.nameAr : m.name;
                return (
                  <motion.li
                    key={m.id}
                    initial={{ opacity: 0, y: -14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, delay: 0.15 + i * 0.12, ease: EASE }}
                    className="flex items-center gap-3 rounded-xl bg-white/[0.04] border border-white/10 p-3"
                  >
                    <img src={m.photo} alt={name} className="w-11 h-11 rounded-xl object-cover flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-extrabold text-white truncate">{name}</div>
                      <div className="text-xs font-semibold text-rose-light truncate">{isRTL ? m.roleAr : m.role}</div>
                    </div>
                    <a
                      href={m.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${name} LinkedIn`}
                      className="w-9 h-9 rounded-lg border border-white/10 text-white/50 hover:text-white hover:border-white/30 flex items-center justify-center transition-colors flex-shrink-0"
                    >
                      <Linkedin size={15} />
                    </a>
                  </motion.li>
                );
              })}
            </ul>
          </div>

          <div className="mt-5 pt-4 border-t border-white/10 flex flex-wrap items-center justify-between gap-3">
            <Link to="/about" className="link-arrow group text-white hover:text-rose-light">
              {t("nav.fullStory")}
              <ArrowRight
                size={14}
                className={`transition-transform duration-300 group-hover:translate-x-1 rtl:rotate-180`}
              />
            </Link>
            <Link to="/contact" className="btn-rose !px-4 !py-2.5">
              {t("cta.button")}
            </Link>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default BehindTheLightMenu;
